/**
 * Audit QMS Detail — Full Lifecycle UI
 * PRD: docs/PRD_Audit_Lifecycle_v2.md Section 7.2
 * 
 * Single-page audit management with all sections:
 * - General info, Instruments, Witnesses, Findings, Conclusions, Recommendations, Actions, Result
 * 
 * Routes: /conformitate/audituri/new | /conformitate/audituri/:auditId
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeft, Save, Play, CheckCircle, XCircle, RotateCcw, 
  Plus, Trash2, FileText, Users, Target, Calendar, User, Printer
} from 'lucide-react';
import { auditServiceQMS, AUDIT_TYPES, AUDIT_QMS_RESULTS } from '../../services/auditService_qms';
import { assessmentInstanceService } from '../../services/assessmentInstanceService';
import assessmentInstrumentService from '../../services/assessmentInstrumentService';
import { useNotifications } from '../NotificationSystem';
import OrganizationalScopeSelector from '../ui/OrganizationalScopeSelector';
import Modal from '../ui/Modal';
import { useOrg } from '../../hooks/useOrg';
import { resolveAuditInstrumentComponent } from '../../utils/resolveAuditInstrumentComponent';
import {
  formatAssessmentAnswer,
  answerDisplayClass,
  getRawAnswerValue
} from '../../utils/assessmentAnswerDisplay';
import AuditThemeSelector from './AuditThemeSelector';
import FindingsCRUD from './FindingsCRUD';
import StarRating from './StarRating';
import PhotoUploader from './PhotoUploader';
import { getLetterheadHtml } from '../../utils/printUtils';
import { getLetterheadUrl } from '../../services/letterheadService';
import './AuditQMSDetail.css';

const AuditQMSDetail = ({ userId, userName, auditId, onBack, onAuditCreated }) => {
  const { t } = useTranslation(['qms', 'common']);
  const { notify } = useNotifications();
  const {
    activeOrgId: organizationId,
    isLoading: orgLoading,
    activeOrg
  } = useOrg();

  const isNew = auditId === 'new';
  
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    auditType: 'internal',
    title: '',
    objective: '',
    organizationalScope: null,
    plannedDate: new Date().toISOString().slice(0, 10),
    auditorId: userId,
    auditorName: userName || '',
    witnesses: [],
    assessmentInstances: [],
    findings: '',
    conclusions: '',
    recommendations: '',
    actions: [],
    result: null,
    nextAuditDate: '',
    nextAuditNotes: '',
    auditThemeIds: [],
    auditThemeLabels: [],
    headNurseId: '',
    headNurseName: '',
    observatiiGenerale: [],
    puncteForte: [],
    puncteImbunatatit: [],
    oportunitatiImbunatatire: [],
    riscuriIdentificate: [],
    rating: null,
    photos: [],
    notificationRecipients: { to: [], cc: [] }
  });

  // UI state
  const [showInstrumentModal, setShowInstrumentModal] = useState(false);
  const [showWitnessModal, setShowWitnessModal] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenNote, setReopenNote] = useState('');
  const [activeAssessment, setActiveAssessment] = useState(null);
  const [expandedInstruments, setExpandedInstruments] = useState({});
  
  // Available instruments
  const [instruments, setInstruments] = useState([]);
  const [loadingInstruments, setLoadingInstruments] = useState(false);
  
  // Available users for auditor/witness/action assignment
  const [users, setUsers] = useState([]);
  const [emailDraftTo, setEmailDraftTo] = useState('');
  const [emailDraftCc, setEmailDraftCc] = useState('');

  useEffect(() => {
    if (!isNew && auditId && auditId !== 'new') {
      loadAudit();
    }
    loadInstruments();
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditId]);

  useEffect(() => {
    if (isNew || !audit) return;
    if (!form.organizationalScope?.nodeId || !users.length) return;
    if (form.notificationRecipients?.to?.length > 0) return;
    const nodeId = form.organizationalScope.nodeId;
    const toUsers = users.filter(
      (u) =>
        u.primary_department_id === nodeId &&
        ['department_head', 'ic_personnel'].includes(u.role_id)
    );
    const auditorUser = users.find((u) => u.id === form.auditorId);
    setForm((prev) => ({
      ...prev,
      notificationRecipients: {
        to: toUsers.map((u) => u.email).filter(Boolean),
        cc: auditorUser?.email ? [auditorUser.email] : []
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audit, isNew, form.organizationalScope?.nodeId, form.auditorId, users]);

  const loadAudit = async () => {
    setLoading(true);
    try {
      const data = await auditServiceQMS.getById(auditId);
      if (!data) {
        notify.error(t('qms:auditNotFound') || 'Audit not found');
        onBack?.();
        return;
      }
      setAudit(data);
      const photosHydrated = (data.photos || []).map((p) => ({
        ...p,
        uploadedAt: p.uploadedAt?.toDate ? p.uploadedAt : p.uploadedAt
      }));
      setForm({
        auditType: data.auditType,
        title: data.title,
        objective: data.objective,
        organizationalScope: data.organizationalScope,
        plannedDate: data.plannedDate ? new Date(data.plannedDate).toISOString().slice(0, 10) : '',
        auditorId: data.auditorId,
        auditorName: data.auditorName,
        witnesses: data.witnesses || [],
        assessmentInstances: data.assessmentInstances || [],
        findings: data.findings || '',
        conclusions: data.conclusions || '',
        recommendations: data.recommendations || '',
        actions: data.actions || [],
        result: data.result,
        nextAuditDate: data.nextAuditDate ? new Date(data.nextAuditDate).toISOString().slice(0, 10) : '',
        nextAuditNotes: data.nextAuditNotes || '',
        auditThemeIds: data.auditThemeIds || [],
        auditThemeLabels: data.auditThemeLabels || [],
        headNurseId: data.headNurseId || '',
        headNurseName: data.headNurseName || '',
        observatiiGenerale: data.observatiiGenerale || [],
        puncteForte: data.puncteForte || [],
        puncteImbunatatit: data.puncteImbunatatit || [],
        oportunitatiImbunatatire: data.oportunitatiImbunatatire || [],
        riscuriIdentificate: data.riscuriIdentificate || [],
        rating: data.rating ?? null,
        photos: photosHydrated,
        notificationRecipients: data.notificationRecipients || { to: [], cc: [] }
      });
    } catch (err) {
      console.error('[AuditQMSDetail] Load failed:', err);
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadInstruments = async () => {
    setLoadingInstruments(true);
    try {
      const data = await assessmentInstrumentService.listInstruments({ status: 'Active' });
      // Exclude IPCAF_2019 in v1 (no scope support)
      const filtered = data.filter((inst) => inst.id !== 'IPCAF_2019');
      setInstruments(filtered);
    } catch (err) {
      console.error('[AuditQMSDetail] Load instruments failed:', err);
    } finally {
      setLoadingInstruments(false);
    }
  };

  const loadUsers = async () => {
    try {
      const { userService } = await import('../../services/firebaseService');
      const allUsers = await userService.getAll();
      const activeUsers = allUsers
        .filter((u) => u.status === 'active')
        .map((u) => ({
          ...u,
          firstName: u.firstName ?? u.first_name ?? '',
          lastName: u.lastName ?? u.last_name ?? '',
          email: u.email ?? '',
          primary_department_id: u.primary_department_id ?? u.primaryDepartmentId ?? null,
          role_id: u.role_id ?? u.roleId ?? ''
        }));
      if (activeUsers.length && process.env.NODE_ENV === 'development') {
        const u0 = activeUsers[0];
        if (!u0.primary_department_id || !u0.role_id) {
          console.warn('[AuditQMSDetail] User shape may miss primary_department_id / role_id for notification auto-fill');
        }
      }
      setUsers(activeUsers);
    } catch (err) {
      console.error('[AuditQMSDetail] Load users failed:', err);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      if (isNew) {
        // Create audit with basic info first
        const auditData = {
          auditType: form.auditType,
          title: form.title,
          objective: form.objective,
          organizationalScope: form.organizationalScope,
          plannedDate: form.plannedDate,
          auditorId: form.auditorId,
          auditorName: form.auditorName,
          witnesses: [],
          assessmentInstances: [],
          auditThemeIds: form.auditThemeIds,
          auditThemeLabels: form.auditThemeLabels,
          headNurseId: form.headNurseId || null,
          headNurseName: form.headNurseName,
          observatiiGenerale: form.observatiiGenerale,
          puncteForte: form.puncteForte,
          puncteImbunatatit: form.puncteImbunatatit,
          oportunitatiImbunatatire: form.oportunitatiImbunatatire,
          riscuriIdentificate: form.riscuriIdentificate,
          rating: form.rating,
          photos: form.photos,
          notificationRecipients: form.notificationRecipients
        };
        
        const newAuditId = await auditServiceQMS.create(organizationId, auditData, userId);
        
        // Then add instruments if any
        for (const inst of form.assessmentInstances) {
          const instanceId = await assessmentInstanceService.create(
            newAuditId,
            inst.instrumentId,
            inst.selectedThemeIds,
            organizationId,
            form.organizationalScope,
            userId
          );
          
          await auditServiceQMS.addAssessmentInstance(newAuditId, {
            instanceId,
            instrumentId: inst.instrumentId,
            instrumentName: inst.instrumentName,
            selectedThemeIds: inst.selectedThemeIds,
            selectedThemes: inst.selectedThemes,
            status: 'pending',
            resultSummary: null
          }, userId);
        }
        
        // Then add witnesses if any
        for (const witness of form.witnesses) {
          const witnessData = { ...witness };
          delete witnessData.id;
          await auditServiceQMS.addWitness(newAuditId, witnessData, userId);
        }

        notify.success(t('qms:auditCreated') || 'Audit created');
        if (typeof onAuditCreated === 'function') {
          onAuditCreated(newAuditId);
        } else {
          onBack?.();
        }
      } else {
        await auditServiceQMS.update(auditId, form, userId);
        await loadAudit();
      }
    } catch (err) {
      console.error('[AuditQMSDetail] Save failed:', err);
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const validateForm = () => {
    if (!form.title || form.title.trim().length < 5) {
      notify.error(t('qms:titleRequired') || 'Title is required (min 5 characters)');
      return false;
    }
    if (!form.plannedDate) {
      notify.error(t('qms:plannedDateRequired') || 'Planned date is required');
      return false;
    }
    if (!form.auditorId) {
      notify.error(t('qms:auditorRequired') || 'Auditor is required');
      return false;
    }
    if (!form.organizationalScope?.nodeId) {
      notify.error(t('qms:organizationalScopeRequired') || 'Organizational scope is required');
      return false;
    }
    if (form.auditType === 'internal' && !form.headNurseId) {
      notify.error(t('qms:headNurseRequired'));
      return false;
    }
    return true;
  };

  const handleTransitionStatus = async (newStatus) => {
    if (!auditId || isNew) return;

    try {
      // If closing audit, save current form data first
      if (newStatus === 'inchis') {
        if (!form.result) {
          notify.error(t('qms:resultRequired') || 'Please select a result before closing the audit');
          return;
        }
        if (!form.conclusions || form.conclusions.trim().length < 20) {
          notify.error(t('qms:conclusionsRequired') || 'Conclusions must be at least 20 characters');
          return;
        }
        if (!form.rating) {
          notify.error(t('qms:ratingRequired'));
          return;
        }

        // Save the audit with current form data
        await auditServiceQMS.update(auditId, form, userId);
      }

      const extraData = {};
      if (newStatus === 'redeschis') {
        if (!reopenNote || reopenNote.trim().length < 10) {
          notify.error(t('qms:reopenNoteRequired') || 'Reopen note is required (min 10 characters)');
          return;
        }
        extraData.reopenNote = reopenNote;
      }

      await auditServiceQMS.transitionStatus(auditId, newStatus, extraData, userId);
      setShowReopenModal(false);
      setReopenNote('');
      await loadAudit();
    } catch (err) {
      console.error('[AuditQMSDetail] Transition failed:', err);
      notify.error(err.message);
    }
  };

  const handleAddInstrument = async (instrumentId, selectedThemeIds) => {
    try {
      const instrument = instruments.find((i) => i.id === instrumentId);
      if (!instrument) throw new Error('Instrument not found');
      
      const selectedThemes = (instrument.components || instrument.themes || [])
        .filter((t) => selectedThemeIds.includes(t.id));
      
      const newInstance = {
        instanceId: isNew ? `temp_${Date.now()}` : null,
        instrumentId,
        instrumentName: instrument.nameRo || instrument.name,
        selectedThemeIds,
        selectedThemes: selectedThemes.map((t) => ({ id: t.id, name: t.nameRo || t.name })),
        status: 'pending',
        resultSummary: null
      };
      
      if (isNew) {
        // For new audits, just add to local state
        setForm({
          ...form,
          assessmentInstances: [...form.assessmentInstances, newInstance]
        });
        notify.success(t('qms:instrumentAdded') || 'Instrument added');
        setShowInstrumentModal(false);
      } else {
        // For existing audits, save to Firestore
        const instanceId = await assessmentInstanceService.create(
          auditId,
          instrumentId,
          selectedThemeIds,
          organizationId,
          form.organizationalScope,
          userId
        );
        
        newInstance.instanceId = instanceId;
        
        await auditServiceQMS.addAssessmentInstance(auditId, newInstance, userId);
        notify.success(t('qms:instrumentAdded') || 'Instrument added');
        setShowInstrumentModal(false);
        await loadAudit();
      }
    } catch (err) {
      console.error('[AuditQMSDetail] Add instrument failed:', err);
      notify.error(err.message);
    }
  };

  const handleRemoveInstrument = async (instanceId) => {
    const inst = form.assessmentInstances?.find((i) => i.instanceId === instanceId);
    if (inst?.status === 'completed') {
      const ok = window.confirm(
        t('qms:removeCompletedInstrumentConfirm') ||
          'This instrument is completed. Remove it from the audit? The assessment data will be deleted.'
      );
      if (!ok) return;
    }
    if (isNew) {
      // For new audits, just remove from local state
      setForm({
        ...form,
        assessmentInstances: form.assessmentInstances.filter(inst => inst.instanceId !== instanceId)
      });
      notify.success(t('qms:instrumentRemoved') || 'Instrument removed');
    } else {
      // For existing audits, remove from Firestore
      try {
        await auditServiceQMS.removeAssessmentInstance(auditId, instanceId, userId);
        if (instanceId && !String(instanceId).startsWith('temp_')) {
          try {
            await assessmentInstanceService.deleteInstance(instanceId);
          } catch (delErr) {
            console.warn(
              '[AuditQMSDetail] assessment_instances delete:',
              instanceId,
              delErr
            );
          }
        }
        notify.success(t('qms:instrumentRemoved') || 'Instrument removed');
        await loadAudit();
      } catch (err) {
        console.error('[AuditQMSDetail] Remove instrument failed:', err);
        notify.error(err.message);
      }
    }
  };

  const handleOpenAssessment = (instance) => {
    if (!auditId || isNew) {
      notify.error(t('qms:saveAuditFirst') || 'Please save the audit before starting assessments');
      return;
    }
    setActiveAssessment(instance);
  };

  const handleCloseAssessment = () => {
    setActiveAssessment(null);
    // Reload audit to get updated assessment instance status
    if (!isNew) {
      loadAudit();
    }
  };

  const loadInstrumentDetails = async (instanceId) => {
    try {
      const instanceData = await assessmentInstanceService.getById(instanceId);
      const instrumentData = await assessmentInstrumentService.getInstrument(instanceData.instrumentId);
      
      if (!instrumentData) {
        throw new Error('Instrument not found');
      }
      
      setExpandedInstruments(prev => ({
        ...prev,
        [instanceId]: { instance: instanceData, instrument: instrumentData }
      }));
    } catch (err) {
      console.error('[AuditQMSDetail] Load details failed:', err);
      notify.error(err.message);
    }
  };

  // Auto-load details for completed assessments
  useEffect(() => {
    if (form.assessmentInstances && form.assessmentInstances.length > 0) {
      form.assessmentInstances.forEach((instance) => {
        if (instance.status === 'completed' && !expandedInstruments[instance.instanceId]) {
          loadInstrumentDetails(instance.instanceId);
        }
      });
    }
  }, [form.assessmentInstances]);

  const handlePrintAudit = async () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      notify.error(t('qms:popupBlocked') || 'Please allow popups to print');
      return;
    }

    let letterheadUrl = activeOrg?.letterheadImageUrl || null;
    if (organizationId) {
      try {
        const fresh = await getLetterheadUrl(organizationId);
        if (fresh) letterheadUrl = fresh;
      } catch (e) {
        console.warn('[AuditQMSDetail] letterhead fetch for print:', e);
      }
    }

    /** Full instance + instrument for print (do not depend on UI expanded state). */
    let instrumentPrintBundles = [];
    const aiList = form.assessmentInstances || [];
    if (aiList.length > 0) {
      try {
        instrumentPrintBundles = await Promise.all(
          aiList.map(async (summaryInst) => {
            try {
              const instance = await assessmentInstanceService.getById(
                summaryInst.instanceId
              );
              const iid =
                instance?.instrumentId || summaryInst.instrumentId;
              const instrument = iid
                ? await assessmentInstrumentService.getInstrument(iid)
                : null;
              return { summary: summaryInst, instance, instrument };
            } catch (e) {
              console.warn(
                '[AuditQMSDetail] print: load instance/instrument',
                summaryInst?.instanceId,
                e
              );
              return {
                summary: summaryInst,
                instance: null,
                instrument: null
              };
            }
          })
        );
      } catch (e) {
        console.warn('[AuditQMSDetail] print bundles:', e);
        instrumentPrintBundles = [];
      }
    }

    const printContent = generatePrintHTML(
      letterheadUrl,
      instrumentPrintBundles
    );

    printWindow.document.write(printContent);
    printWindow.document.close();

    let printTriggered = false;
    const triggerPrint = () => {
      if (printTriggered) return;
      printTriggered = true;
      try {
        printWindow.focus();
        printWindow.print();
      } catch (e) {
        console.warn('[AuditQMSDetail] print:', e);
      }
    };

    let waitImagesStarted = false;
    const waitImagesThenPrint = () => {
      if (waitImagesStarted) return;
      waitImagesStarted = true;
      const imgs = [...printWindow.document.images];
      if (imgs.length === 0) {
        triggerPrint();
        return;
      }
      let pending = 0;
      imgs.forEach((img) => {
        if (img.complete) return;
        pending += 1;
        const done = () => {
          pending -= 1;
          if (pending <= 0) triggerPrint();
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      });
      if (pending === 0) triggerPrint();
    };

    printWindow.onload = () => waitImagesThenPrint();
    setTimeout(() => {
      if (printWindow.document.readyState === 'complete') {
        waitImagesThenPrint();
      }
    }, 600);
  };

  const generatePrintHTML = (
    letterheadImageUrlOverride,
    instrumentPrintBundles = []
  ) => {
    const letterheadUrl =
      letterheadImageUrlOverride ??
      activeOrg?.letterheadImageUrl ??
      null;

    const esc = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    /** Same idea as checklist scope: if stored IDs do not match component ids, show full instrument. */
    const resolvePrintComponents = (instance, instrument) => {
      const components = instrument?.components || [];
      if (!components.length) return [];
      const raw = instance?.selectedThemeIds || [];
      const ids = new Set(raw.filter(Boolean));
      if (ids.size === 0) return components;
      const matched = components.filter((c) => ids.has(c.id));
      return matched.length > 0 ? matched : components;
    };

    const answerDisplay = (answersMap, question) =>
      formatAssessmentAnswer(answersMap, question, true);

    const bundleForSummary = (summaryInst) => {
      const fromFetch = instrumentPrintBundles.find(
        (b) => b?.summary?.instanceId === summaryInst.instanceId
      );
      if (fromFetch?.instance && fromFetch?.instrument) return fromFetch;
      const expanded = expandedInstruments[summaryInst.instanceId];
      if (expanded?.instance && expanded?.instrument) {
        return {
          summary: summaryInst,
          instance: expanded.instance,
          instrument: expanded.instrument
        };
      }
      return null;
    };
    const statusLabel = t(`qms:auditStatus.${audit?.status}`) || audit?.status;
    const typeLabel = AUDIT_TYPES.find(type => type === form.auditType) 
      ? t(`qms:auditType_${form.auditType}`) || form.auditType 
      : form.auditType;
    const resultLabel = form.result ? (t(`qms:auditResult.${form.result}`) || form.result) : '—';
    let objectivePrint = '—';
    if (form.auditThemeLabels?.length) {
      objectivePrint = form.auditThemeLabels.map((x) => esc(x)).join(', ');
    } else if (form.objective) {
      objectivePrint = esc(form.objective);
    }
    const structuredRows = [
      { title: t('qms:observatiiGenerale'), items: form.observatiiGenerale },
      { title: t('qms:puncteForte'), items: form.puncteForte },
      { title: t('qms:puncteImbunatatit'), items: form.puncteImbunatatit },
      { title: t('qms:oportunitatiImbunatatire'), items: form.oportunitatiImbunatatire },
      { title: t('qms:riscuriIdentificate'), items: form.riscuriIdentificate }
    ];
    const anyStructured = structuredRows.some((r) => r.items?.length);
    let findingsBlock = '';
    if (anyStructured) {
      findingsBlock = `
    <div class="section">
      <h2>${esc(t('qms:structuredFindingsSection'))}</h2>
      ${structuredRows
        .filter((r) => r.items?.length)
        .map(
          (r) => `
      <h3>${esc(r.title)}</h3>
      <ul style="margin:0 0 10px 18px;padding:0;font-size:9pt;">
        ${r.items.map((it) => `<li>${esc(it)}</li>`).join('')}
      </ul>`
        )
        .join('')}
    </div>`;
    } else if (form.findings) {
      findingsBlock = `
    <div class="section">
      <h2>Constatări</h2>
      <div class="text-content">${esc(form.findings)}</div>
    </div>`;
    }
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${esc(form.title || 'Audit')}</title>
  <style>
    @page { 
      margin: 1.5cm 2cm;
      size: A4;
    }
    * { box-sizing: border-box; }
    html, body {
      max-width: 100%;
      overflow-x: hidden;
    }
    body { 
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      font-size: 10pt; 
      line-height: 1.3;
      color: #1a1a1a;
      margin: 0;
      padding: 0;
    }
    .container { max-width: 100%; width: 100%; }
    
    /* Header — keep inside printable area (no negative margins → no fake “screenshot” crop) */
    .header { 
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 12px 16px;
      margin: 0 0 12px 0;
      width: 100%;
      border-radius: 4px;
    }
    .header h1 { 
      font-size: 14pt;
      margin: 0 0 6px 0;
      font-weight: 600;
    }
    .header-grid { 
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      font-size: 8.5pt;
    }
    .header-item { opacity: 0.95; }
    .header-label { 
      font-weight: 600;
      opacity: 0.8;
      display: block;
      margin-bottom: 1px;
      font-size: 7.5pt;
    }
    
    /* Sections */
    h2 { 
      font-size: 11pt;
      margin: 10px 0 6px 0;
      color: #667eea;
      font-weight: 600;
      border-bottom: 2px solid #667eea;
      padding-bottom: 2px;
    }
    h3 { 
      font-size: 10pt;
      margin: 8px 0 5px 0;
      color: #333;
      font-weight: 600;
    }
    
    .section { 
      margin-bottom: 12px;
    }
    
    .info-box {
      background: #f8f9fa;
      border-left: 4px solid #667eea;
      padding: 8px 10px;
      margin: 6px 0;
    }
    .info-row {
      display: flex;
      margin: 3px 0;
    }
    .info-label { 
      font-weight: 600;
      color: #555;
      min-width: 130px;
      font-size: 9pt;
    }
    .info-value { 
      color: #1a1a1a;
      font-size: 9pt;
    }
    
    /* Questions */
    .theme-title { 
      background: #667eea;
      color: white;
      padding: 5px 10px;
      margin: 6px 0 3px 0;
      font-weight: 600;
      font-size: 9.5pt;
    }
    .question-row { 
      display: flex;
      align-items: flex-start;
      padding: 4px 8px;
      border-bottom: 1px solid #e9ecef;
      font-size: 8.5pt;
      line-height: 1.3;
    }
    .question-row:nth-child(even) { background: #f8f9fa; }
    .question-code { 
      font-weight: 600;
      color: #667eea;
      min-width: 50px;
      flex-shrink: 0;
    }
    .question-text { 
      flex: 1;
      padding: 0 8px;
      line-height: 1.4;
    }
    .answer { 
      font-weight: 700;
      min-width: 45px;
      text-align: center;
      padding: 3px 8px;
      border-radius: 3px;
      flex-shrink: 0;
      font-size: 9pt;
    }
    .answer-yes { background: #d1fae5; color: #065f46; }
    .answer-no { background: #fee2e2; color: #991b1b; }
    .answer-na { background: #e9ecef; color: #6c757d; }

    /* Instrument questions — table fits A4, no horizontal scroll */
    .instrument-wrap { margin: 8px 0 14px 0; page-break-inside: avoid; }
    .instrument-q-table {
      width: 100%;
      table-layout: fixed;
      border-collapse: collapse;
      font-size: 8.5pt;
      margin: 4px 0 10px 0;
    }
    .instrument-q-table th,
    .instrument-q-table td {
      border: 1px solid #dee2e6;
      padding: 5px 6px;
      vertical-align: top;
      word-wrap: break-word;
      overflow-wrap: anywhere;
    }
    .instrument-q-table th {
      background: #e9ecef;
      font-weight: 600;
      text-align: left;
    }
    .instrument-q-table .col-code { width: 11%; }
    .instrument-q-table .col-q { width: 72%; }
    .instrument-q-table .col-a { width: 17%; text-align: center; }
    .instrument-q-table tbody tr:nth-child(even) { background: #f8f9fa; }
    .print-instrument-error {
      font-size: 9pt;
      color: #842029;
      background: #f8d7da;
      border: 1px solid #f5c2c7;
      padding: 8px 10px;
      border-radius: 4px;
      margin: 6px 0;
    }
    
    /* Lists */
    .witness-item, .action-item { 
      padding: 8px 10px;
      background: #f8f9fa;
      margin: 4px 0;
      border-left: 3px solid #667eea;
      font-size: 9pt;
    }
    .witness-item strong, .action-item strong { color: #1a1a1a; }
    
    /* Text content */
    .text-content {
      background: #f8f9fa;
      padding: 10px 12px;
      border-radius: 4px;
      white-space: pre-wrap;
      font-size: 9pt;
      line-height: 1.5;
    }
    
    /* Footer */
    .footer { 
      margin-top: 20px;
      padding-top: 10px;
      border-top: 2px solid #e9ecef;
      font-size: 8pt;
      color: #6c757d;
      text-align: center;
    }
    
    @media print {
      html, body { overflow-x: visible !important; max-width: none; }
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .header { break-inside: avoid; }
      .theme-title { break-after: avoid; }
      .instrument-wrap { break-inside: avoid; }
    }
  </style>
</head>
<body>
${getLetterheadHtml(letterheadUrl)}
  <div class="container">
    <div class="header">
      <h1>${esc(form.title || 'Raport Audit')}</h1>
      <div class="header-grid">
        <div class="header-item">
          <span class="header-label">Tip</span>
          ${esc(typeLabel)}
        </div>
        <div class="header-item">
          <span class="header-label">Status</span>
          ${esc(statusLabel)}
        </div>
        <div class="header-item">
          <span class="header-label">Data</span>
          ${form.plannedDate ? new Date(form.plannedDate.toDate ? form.plannedDate.toDate() : form.plannedDate).toLocaleDateString('ro-RO') : '—'}
        </div>
        <div class="header-item">
          <span class="header-label">Auditor</span>
          ${esc(form.auditorName || '—')}
        </div>
      </div>
    </div>

    <div class="info-box">
      <div class="info-row">
        <span class="info-label">Domeniu organizațional:</span>
        <span class="info-value">${esc(form.organizationalScope?.breadcrumbPath?.map(c => c.label).join(' › ') || form.organizationalScope?.nodeLabel || '—')}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Obiectiv / teme:</span>
        <span class="info-value">${objectivePrint}</span>
      </div>
    </div>

    ${form.assessmentInstances && form.assessmentInstances.length > 0 ? `
    <div class="section">
      <h2>Instrumente de evaluare</h2>
      ${form.assessmentInstances.map((summaryInst) => {
        const bundle = bundleForSummary(summaryInst);
        if (!bundle?.instance || !bundle?.instrument) {
          return `
          <div class="instrument-wrap">
            <h3>${esc(summaryInst.instrumentName || 'Instrument')}</h3>
            <p class="print-instrument-error">Datele instrumentului nu au putut fi încărcate pentru tipărire. Reîncercați sau deschideți secțiunea instrumentului înainte de print.</p>
          </div>`;
        }
        const { instance, instrument } = bundle;
        const selectedComponents = resolvePrintComponents(instance, instrument);
        return `
          <div class="instrument-wrap">
          <h3>${esc(summaryInst.instrumentName || instrument.nameRo || instrument.name || 'Instrument')}</h3>
          ${selectedComponents.map((comp) => `
            <div class="theme-title">${esc(comp.code ? `${comp.code}. ` : '')}${esc(comp.nameRo || comp.name || '')}</div>
            <table class="instrument-q-table" role="presentation">
              <thead>
                <tr>
                  <th class="col-code">Cod</th>
                  <th class="col-q">Întrebare</th>
                  <th class="col-a">Răspuns</th>
                </tr>
              </thead>
              <tbody>
            ${(comp.questions || [])
              .map((q) => {
                const answerValue = answerDisplay(instance.answers || {}, q);
                const rawVal = getRawAnswerValue(instance.answers || {}, q);
                const answerClass = answerDisplayClass(rawVal);
                return `
                <tr>
                  <td class="col-code"><strong>${esc(q.code || '')}</strong></td>
                  <td class="col-q">${esc(q.textRo || q.text || '')}</td>
                  <td class="col-a"><span class="answer ${answerClass}">${esc(answerValue)}</span></td>
                </tr>`;
              })
              .join('')}
              </tbody>
            </table>
          `).join('')}
          </div>`;
      }).join('')}
    </div>
    ` : ''}

    ${form.witnesses && form.witnesses.length > 0 ? `
    <div class="section">
      <h2>Martori</h2>
      ${form.witnesses.map(w => `
        <div class="witness-item">
          <strong>${esc(w.name)}</strong> — ${esc(w.role)}${w.type === 'external' ? ' (Extern)' : ''}${w.phoneNumber ? ` • ${esc(w.phoneNumber)}` : ''}
        </div>
      `).join('')}
    </div>
    ` : ''}

    ${findingsBlock}

    ${form.conclusions ? `
    <div class="section">
      <h2>Concluzii</h2>
      <div class="text-content">${esc(form.conclusions)}</div>
    </div>
    ` : ''}

    ${form.recommendations ? `
    <div class="section">
      <h2>Recomandări</h2>
      <div class="text-content">${esc(form.recommendations)}</div>
    </div>
    ` : ''}

    ${form.actions && form.actions.length > 0 ? `
    <div class="section">
      <h2>Acțiuni</h2>
      ${form.actions.map(action => `
        <div class="action-item">
          <div style="margin-bottom: 4px;"><strong>${esc(action.description)}</strong></div>
          <div>Tip: ${esc(t(`qms:actionType.${action.type}`) || action.type)}${action.assignedToName ? ` • Responsabil: ${esc(action.assignedToName)}` : ''}${action.dueDate ? ` • Termen: ${new Date(action.dueDate).toLocaleDateString('ro-RO')}` : ''}</div>
        </div>
      `).join('')}
    </div>
    ` : ''}

    <div class="section">
      <h2>Rezultat audit</h2>
      <div class="info-box">
        <div class="info-row">
          <span class="info-label">Rezultat:</span>
          <span class="info-value" style="font-weight: 600;">${esc(resultLabel)}</span>
        </div>
        ${form.rating ? `
        <div class="info-row">
          <span class="info-label">${esc(t('qms:ratingLabel'))}:</span>
          <span class="info-value">${form.rating}/5</span>
        </div>
        ` : ''}
        ${form.nextAuditDate ? `
        <div class="info-row">
          <span class="info-label">Data audit următor:</span>
          <span class="info-value">${new Date(form.nextAuditDate.toDate ? form.nextAuditDate.toDate() : form.nextAuditDate).toLocaleDateString('ro-RO')}</span>
        </div>
        ` : ''}
        ${form.nextAuditNotes ? `
        <div class="info-row">
          <span class="info-label">Note audit următor:</span>
          <span class="info-value">${esc(form.nextAuditNotes)}</span>
        </div>
        ` : ''}
      </div>
    </div>

    <div class="footer">
      Document generat la ${new Date().toLocaleString('ro-RO', { dateStyle: 'long', timeStyle: 'short' })}
    </div>
  </div>
</body>
</html>
    `;
  };

  const handleAddWitness = async (witness) => {
    const newWitness = {
      ...witness,
      id: isNew ? `temp_${Date.now()}` : witness.id
    };

    if (isNew) {
      // For new audits, just add to local state
      setForm({
        ...form,
        witnesses: [...form.witnesses, newWitness]
      });
      notify.success(t('qms:witnessAdded') || 'Witness added');
      setShowWitnessModal(false);
    } else {
      // For existing audits, save to Firestore
      try {
        await auditServiceQMS.addWitness(auditId, witness, userId);
        notify.success(t('qms:witnessAdded') || 'Witness added');
        setShowWitnessModal(false);
        await loadAudit();
      } catch (err) {
        console.error('[AuditQMSDetail] Add witness failed:', err);
        notify.error(err.message);
      }
    }
  };

  const handleRemoveWitness = async (witnessId) => {
    if (isNew) {
      // For new audits, just remove from local state
      setForm({
        ...form,
        witnesses: form.witnesses.filter(w => w.id !== witnessId)
      });
      notify.success(t('qms:witnessRemoved') || 'Witness removed');
    } else {
      // For existing audits, remove from Firestore
      try {
        await auditServiceQMS.removeWitness(auditId, witnessId, userId);
        notify.success(t('qms:witnessRemoved') || 'Witness removed');
        await loadAudit();
      } catch (err) {
        console.error('[AuditQMSDetail] Remove witness failed:', err);
        notify.error(err.message);
      }
    }
  };

  const handleAddAction = async (action) => {
    if (!auditId || isNew) {
      notify.error(t('qms:saveAuditFirst') || 'Save the audit before adding actions');
      return;
    }
    
    try {
      await auditServiceQMS.addAction(auditId, action, userId);
      notify.success(t('qms:actionAdded') || 'Action added');
      setShowActionModal(false);
      await loadAudit();
    } catch (err) {
      console.error('[AuditQMSDetail] Add action failed:', err);
      notify.error(err.message);
    }
  };

  const handleRemoveAction = async (actionId) => {
    if (!auditId || isNew) return;
    
    try {
      await auditServiceQMS.removeAction(auditId, actionId, userId);
      notify.success(t('qms:actionRemoved') || 'Action removed');
      await loadAudit();
    } catch (err) {
      console.error('[AuditQMSDetail] Remove action failed:', err);
      notify.error(err.message);
    }
  };

  const canEdit = (field) => {
    if (isNew) return true;
    if (!audit) return false;
    
    const status = audit.status;
    
    // Field-specific edit rules per PRD Section 7.2
    const editRules = {
      general: ['planificat', 'in_desfasurare', 'constatari'],
      organizationalScope: ['planificat'],
      // Allow managing instruments during findings / reopened so users can correct scope (PRD-flex)
      instruments: ['planificat', 'in_desfasurare', 'constatari', 'redeschis'],
      witnesses: ['planificat', 'in_desfasurare', 'constatari', 'redeschis'],
      findings: ['in_desfasurare', 'constatari', 'redeschis'],
      conclusions: ['constatari', 'redeschis'],
      recommendations: ['constatari', 'redeschis'],
      actions: ['constatari', 'redeschis'],
      result: ['constatari', 'redeschis']
    };
    
    return editRules[field]?.includes(status) || false;
  };

  const getPrimaryAction = () => {
    /** Always return an action object — button stays visible; use disabled + title instead of hiding. */
    if (isNew) {
      return {
        label: t('qms:startAudit') || 'Start Audit',
        icon: Play,
        action: () =>
          notify.info(t('qms:saveAuditFirst') || 'Save the audit first'),
        disabled: true,
        hint: t('qms:saveAuditFirst') || 'Save the audit first'
      };
    }
    if (!audit) {
      return {
        label: t('qms:startAudit') || 'Start Audit',
        icon: Play,
        action: () => {},
        disabled: true,
        hint: t('common:loading') || 'Loading...'
      };
    }

    const status = audit.status;
    
    if (status === 'planificat') {
      const canStart = form.assessmentInstances.length >= 1;
      return {
        label: t('qms:startAudit') || 'Start Audit',
        icon: Play,
        action: () => handleTransitionStatus('in_desfasurare'),
        disabled: !canStart,
        hint: !canStart ? (t('qms:addInstrumentFirst') || 'Add at least one instrument') : null
      };
    }
    
    if (status === 'in_desfasurare') {
      const allCompleted = form.assessmentInstances.every((inst) => inst.status === 'completed');
      return {
        label: t('qms:finalizeAssessments') || 'Finalize Assessments',
        icon: CheckCircle,
        action: () => handleTransitionStatus('constatari'),
        disabled: !allCompleted,
        hint: !allCompleted ? (t('qms:completeAllAssessments') || 'Complete all assessments first') : null
      };
    }
    
    if (status === 'constatari') {
      const canClose = form.result && form.conclusions.trim().length >= 20 && form.rating;
      return {
        label: t('qms:closeAudit') || 'Close Audit',
        icon: CheckCircle,
        action: () => handleTransitionStatus('inchis'),
        disabled: !canClose,
        hint: !canClose
          ? !form.rating
            ? t('qms:ratingRequired')
            : t('qms:resultAndConclusionsRequired') || 'Result and conclusions (min 20 chars) required'
          : null
      };
    }
    
    if (status === 'inchis') {
      return {
        label: t('qms:reopenAudit') || 'Reopen Audit',
        icon: RotateCcw,
        action: () => setShowReopenModal(true),
        disabled: false,
        hint: null
      };
    }
    
    if (status === 'redeschis') {
      const canClose = form.result && form.conclusions.trim().length >= 20 && form.rating;
      return {
        label: t('qms:closeAudit') || 'Close Audit',
        icon: CheckCircle,
        action: () => handleTransitionStatus('inchis'),
        disabled: !canClose,
        hint: !canClose
          ? !form.rating
            ? t('qms:ratingRequired')
            : t('qms:resultAndConclusionsRequired') || 'Result and conclusions required'
          : null
      };
    }

    return {
      label: t('qms:startAudit') || 'Start Audit',
      icon: Play,
      action: () =>
        notify.info(t('qms:unexpectedAuditStatus') || 'Unexpected audit status'),
      disabled: true,
      hint: String(status || '')
    };
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      planificat: 'status-badge-planificat',
      in_desfasurare: 'status-badge-in-desfasurare',
      constatari: 'status-badge-constatari',
      inchis: 'status-badge-inchis',
      redeschis: 'status-badge-redeschis'
    };
    return classes[status] || 'status-badge-default';
  };

  const getStatusLabel = (status) => {
    return t(`qms:auditStatus.${status}`) || status;
  };

  const primaryAction = getPrimaryAction();

  if (orgLoading || loading) {
    return (
      <div className="audit-qms-detail">
        <div className="aqd-loading">{t('common:loading') || 'Loading...'}</div>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="audit-qms-detail">
        <div className="aqd-loading" role="alert">
          {t('qms:noOrganizationContext') || 'Selectează o organizație pentru a continua.'}
        </div>
      </div>
    );
  }

  // If an assessment is active, render the instrument-specific evaluation UI
  if (activeAssessment) {
    const AssessmentComponent = resolveAuditInstrumentComponent(
      activeAssessment.instrumentId
    );
    const auditContext = {
      auditId: auditId,
      instanceId: activeAssessment.instanceId,
      selectedThemeIds: activeAssessment.selectedThemeIds,
      auditTitle: form.title,
      organizationalScope: form.organizationalScope
    };
    const assessmentProps = {
      organizationId,
      userId,
      userName,
      auditContext,
      onComplete: handleCloseAssessment,
      onBack: handleCloseAssessment
    };

    if (!AssessmentComponent) {
      return (
        <div className="audit-qms-detail">
          <div className="aqd-loading" role="alert">
            {t('qms:unsupportedAuditInstrument') ||
              'Acest instrument nu este încă disponibil în fluxul de audit. Folosiți Checklist 753 sau HHSAF.'}
          </div>
          <button type="button" className="btn-back" onClick={handleCloseAssessment}>
            <ArrowLeft size={20} />
            {t('common:back') || 'Back'}
          </button>
        </div>
      );
    }

    return <AssessmentComponent {...assessmentProps} />;
  }

  return (
    <div className="audit-qms-detail">
      <div className="aqd-header">
        <button 
          type="button" 
          className="btn-back"
          onClick={onBack}
        >
          <ArrowLeft size={20} />
          {t('common:back') || 'Back'}
        </button>
        
        <div className="aqd-header-main">
          <div className="aqd-header-left">
            <h1>{isNew ? (t('qms:newAudit') || 'New Audit') : form.title}</h1>
            {!isNew && audit && (
              <span className={`status-badge ${getStatusBadgeClass(audit.status)}`}>
                {getStatusLabel(audit.status)}
              </span>
            )}
          </div>
          
          <div className="aqd-header-actions">
            {!isNew && audit?.status === 'planificat' && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={async () => {
                  if (window.confirm(t('qms:confirmDeleteAudit') || 'Delete this audit?')) {
                    try {
                      await auditServiceQMS.delete(auditId, userId);
                      notify.success(t('qms:auditDeleted') || 'Audit deleted');
                      onBack?.();
                    } catch (err) {
                      notify.error(err.message);
                    }
                  }
                }}
              >
                <Trash2 size={16} />
                {t('common:delete') || 'Delete'}
              </button>
            )}
            
            {(isNew || audit) && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSave}
                disabled={saving}
              >
                <Save size={18} />
                {saving
                  ? (t('common:saving') || 'Saving...')
                  : isNew
                    ? (t('qms:createAudit') || 'Create audit')
                    : (t('common:save') || 'Save')}
              </button>
            )}

            <button
              type="button"
              className="btn btn-secondary"
              onClick={handlePrintAudit}
              disabled={isNew}
              title={
                isNew
                  ? (t('qms:saveAuditFirst') || 'Save the audit first')
                  : undefined
              }
            >
              <Printer size={18} />
              {t('qms:printAudit') || 'Print'}
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={primaryAction.action}
              disabled={primaryAction.disabled}
              title={primaryAction.hint || undefined}
            >
              <primaryAction.icon size={18} />
              {primaryAction.label}
            </button>
          </div>
        </div>
      </div>

      <div className="aqd-content">
        {/* Section 1: General Information */}
        <section className="aqd-section">
          <h3 className="aqd-section-title">
            <FileText size={20} />
            {t('qms:generalInformation') || 'General Information'}
          </h3>
          
          <div className="aqd-form-grid">
            <div className="form-field">
              <label className="form-label required">{t('qms:auditType') || 'Audit Type'}</label>
              <select
                value={form.auditType}
                onChange={(e) => setForm({ ...form, auditType: e.target.value })}
                disabled={!canEdit('general')}
                className="form-input"
              >
                {AUDIT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`qms:auditType_${type}`) || type}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-field full-width">
              <label className="form-label required">{t('qms:title') || 'Title'}</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                disabled={!canEdit('general')}
                className="form-input"
                placeholder={t('qms:titlePlaceholder') || 'e.g., Hand Hygiene Audit ICU - Q1 2026'}
                minLength={5}
                maxLength={200}
              />
            </div>

            <div className="form-field full-width">
              <label className="form-label required">{t('qms:auditThemes')}</label>
              <AuditThemeSelector
                hospitalId={organizationId}
                value={form.auditThemeIds}
                onChange={(ids, labels) =>
                  setForm({ ...form, auditThemeIds: ids, auditThemeLabels: labels })
                }
                disabled={!canEdit('general')}
              />
            </div>
            {form.auditThemeIds.length === 0 && form.objective && (
              <div className="form-field full-width">
                <label className="form-label">{t('qms:legacyObjective')}</label>
                <div className="form-input" style={{ padding: '10px 12px', background: '#f9fafb' }}>
                  {form.objective}
                </div>
              </div>
            )}

            <div className="form-field full-width">
              <label className="form-label required">{t('qms:organizationalScope') || 'Organizational Scope'}</label>
              <OrganizationalScopeSelector
                value={form.organizationalScope}
                onChange={(scope) => setForm({ ...form, organizationalScope: scope })}
                disabled={!canEdit('organizationalScope')}
              />
            </div>

            <div className="form-field">
              <label className="form-label required">{t('qms:plannedDate') || 'Planned Date'}</label>
              <input
                type="date"
                value={form.plannedDate}
                onChange={(e) => setForm({ ...form, plannedDate: e.target.value })}
                disabled={!canEdit('general')}
                className="form-input"
              />
            </div>

            <div className="form-field">
              <label className="form-label required">{t('qms:auditor') || 'Auditor'}</label>
              <div className="form-input" style={{ padding: '10px 12px', background: '#f9fafb', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                {userName || 'Current User'}
              </div>
              <input type="hidden" value={form.auditorId} />
              <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                {t('qms:auditorNote') || 'Auditul este inițiat de tine. Poți adăuga martori în secțiunea dedicată.'}
              </small>
            </div>

            <div className="form-field">
              <label className={`form-label ${form.auditType === 'internal' ? 'required' : ''}`}>
                {t('qms:headNursePresent')}
              </label>
              <select
                value={form.headNurseId}
                onChange={(e) => {
                  const user = users.find((u) => u.id === e.target.value);
                  setForm({
                    ...form,
                    headNurseId: e.target.value,
                    headNurseName: user ? `${user.firstName} ${user.lastName}`.trim() : ''
                  });
                }}
                disabled={!canEdit('general')}
                className="form-input"
              >
                <option value="">{t('common:select')}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        {/* Section 2: Assessment Instruments */}
        <section className="aqd-section">
          <div className="aqd-section-header">
            <h3 className="aqd-section-title">
              <Target size={20} />
              {t('qms:assessmentInstruments') || 'Assessment Instruments'}
            </h3>
            {canEdit('instruments') && (
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setShowInstrumentModal(true)}
              >
                <Plus size={16} />
                {t('qms:addInstrument') || 'Add Instrument'}
              </button>
            )}
          </div>

          {form.assessmentInstances.length === 0 ? (
            <div className="aqd-empty-state">
              <p>{t('qms:noInstrumentsYet') || 'No instruments added yet'}</p>
            </div>
          ) : (
            <div className="aqd-instruments-list">
              {form.assessmentInstances.map((instance) => {
                const expandedData = expandedInstruments[instance.instanceId];
                const isCompleted = instance.status === 'completed';
                
                return (
                  <div key={instance.instanceId} className="instrument-wrapper">
                    <InstrumentCard
                      instance={instance}
                      canEdit={canEdit('instruments')}
                      canComplete={audit?.status === 'in_desfasurare'}
                      onRemove={() => handleRemoveInstrument(instance.instanceId)}
                      onComplete={() => handleOpenAssessment(instance)}
                      t={t}
                    />
                    
                    {isCompleted && expandedData && (
                      <InstrumentDetailsView
                        instance={expandedData.instance}
                        instrument={expandedData.instrument}
                        t={t}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Section 3: Witnesses */}
        <section className="aqd-section">
          <div className="aqd-section-header">
            <h3 className="aqd-section-title">
              <Users size={20} />
              {t('qms:witnesses') || 'Witnesses'}
            </h3>
            {canEdit('witnesses') && (
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => setShowWitnessModal(true)}
              >
                <Plus size={16} />
                {t('qms:addWitness') || 'Add Witness'}
              </button>
            )}
          </div>

          {form.witnesses.length === 0 ? (
            <div className="aqd-empty-state">
              <p>{t('qms:noWitnessesYet') || 'No witnesses added (optional)'}</p>
            </div>
          ) : (
            <div className="aqd-witnesses-list">
              {form.witnesses.map((witness) => (
                <WitnessCard
                  key={witness.id}
                  witness={witness}
                  canEdit={canEdit('witnesses')}
                  onRemove={() => handleRemoveWitness(witness.id)}
                  t={t}
                />
              ))}
            </div>
          )}
        </section>

        {/* Section 4: Structured findings + legacy summary */}
        {!isNew && ['in_desfasurare', 'constatari', 'redeschis', 'inchis'].includes(audit?.status) && (
          <section className="aqd-section">
            <h3 className="aqd-section-title">{t('qms:structuredFindingsSection')}</h3>
            <div className="aqd-structured-findings-stack">
            <FindingsCRUD
              hospitalId={organizationId}
              category="observatii"
              label={t('qms:observatiiGenerale')}
              value={form.observatiiGenerale}
              onChange={(items) => setForm({ ...form, observatiiGenerale: items })}
              placeholder={t('qms:addObservatie')}
              disabled={!canEdit('findings')}
            />
            <FindingsCRUD
              hospitalId={organizationId}
              category="puncte_forte"
              label={t('qms:puncteForte')}
              value={form.puncteForte}
              onChange={(items) => setForm({ ...form, puncteForte: items })}
              placeholder={t('qms:addPunctFort')}
              disabled={!canEdit('findings')}
            />
            <FindingsCRUD
              hospitalId={organizationId}
              category="imbunatatire"
              label={t('qms:puncteImbunatatit')}
              value={form.puncteImbunatatit}
              onChange={(items) => setForm({ ...form, puncteImbunatatit: items })}
              placeholder={t('qms:addImbunatatire')}
              disabled={!canEdit('findings')}
            />
            <FindingsCRUD
              hospitalId={organizationId}
              category="oportunitati"
              label={t('qms:oportunitatiImbunatatire')}
              value={form.oportunitatiImbunatatire}
              onChange={(items) => setForm({ ...form, oportunitatiImbunatatire: items })}
              placeholder={t('qms:addOportunitate')}
              disabled={!canEdit('findings')}
            />
            <FindingsCRUD
              hospitalId={organizationId}
              category="riscuri"
              label={t('qms:riscuriIdentificate')}
              value={form.riscuriIdentificate}
              onChange={(items) => setForm({ ...form, riscuriIdentificate: items })}
              placeholder={t('qms:addRisc')}
              disabled={!canEdit('findings')}
            />
            </div>
            <h3 className="aqd-section-title" style={{ marginTop: 24 }}>
              {t('qms:findingsSummaryField')}
            </h3>
            <p className="field-hint" style={{ marginBottom: 8 }}>
              {t('qms:findingsLegacyHint')}
            </p>
            <textarea
              value={form.findings}
              onChange={(e) => setForm({ ...form, findings: e.target.value })}
              disabled={!canEdit('findings')}
              className="form-textarea full-width"
              rows={6}
              placeholder={t('qms:findingsPlaceholder')}
            />
          </section>
        )}

        {/* Section 5: Conclusions */}
        {!isNew && ['constatari', 'redeschis', 'inchis'].includes(audit?.status) && (
          <section className="aqd-section">
            <h3 className="aqd-section-title">{t('qms:conclusions') || 'Conclusions'}</h3>
            <textarea
              value={form.conclusions}
              onChange={(e) => setForm({ ...form, conclusions: e.target.value })}
              disabled={!canEdit('conclusions')}
              className="form-textarea full-width"
              rows={6}
              placeholder={t('qms:conclusionsPlaceholder') || 'Summarize key conclusions...'}
            />
            {canEdit('conclusions') && form.conclusions.trim().length < 20 && (
              <p className="field-hint warning">
                {t('qms:conclusionsMinLength') || 'Minimum 20 characters required to close audit'}
              </p>
            )}
          </section>
        )}

        {/* Section 6: Recommendations */}
        {!isNew && ['constatari', 'redeschis', 'inchis'].includes(audit?.status) && (
          <section className="aqd-section">
            <h3 className="aqd-section-title">{t('qms:recommendations') || 'Recommendations'}</h3>
            <textarea
              value={form.recommendations}
              onChange={(e) => setForm({ ...form, recommendations: e.target.value })}
              disabled={!canEdit('recommendations')}
              className="form-textarea full-width"
              rows={6}
              placeholder={t('qms:recommendationsPlaceholder') || 'Provide recommendations for improvement...'}
            />
          </section>
        )}

        {/* Section 7: Actions */}
        {!isNew && ['constatari', 'redeschis', 'inchis'].includes(audit?.status) && (
          <section className="aqd-section">
            <div className="aqd-section-header">
              <h3 className="aqd-section-title">{t('qms:actions') || 'Actions'}</h3>
              {canEdit('actions') && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowActionModal(true)}
                >
                  <Plus size={16} />
                  {t('qms:addAction') || 'Add Action'}
                </button>
              )}
            </div>

            {form.actions.length === 0 ? (
              <div className="aqd-empty-state">
                <p>{t('qms:noActionsYet') || 'No actions defined yet'}</p>
              </div>
            ) : (
              <div className="aqd-actions-list">
                {form.actions.map((action) => (
                  <ActionCard
                    key={action.id}
                    action={action}
                    canEdit={canEdit('actions')}
                    onRemove={() => handleRemoveAction(action.id)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Section 8: Result & Next Audit */}
        {!isNew && ['constatari', 'redeschis', 'inchis'].includes(audit?.status) && (
          <section className="aqd-section">
            <h3 className="aqd-section-title">{t('qms:resultAndNextAudit') || 'Result & Next Audit'}</h3>
            
            <div className="aqd-form-grid">
              <div className="form-field">
                <label className="form-label required">{t('qms:result') || 'Result'}</label>
                <select
                  value={form.result || ''}
                  onChange={(e) => setForm({ ...form, result: e.target.value || null })}
                  disabled={!canEdit('result')}
                  className="form-input"
                >
                  <option value="">{t('common:select') || 'Select...'}</option>
                  {AUDIT_QMS_RESULTS.map((result) => (
                    <option key={result} value={result}>
                      {t(`qms:auditResult.${result}`) || result.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label className="form-label">{t('qms:nextAuditDate') || 'Next Audit Date'}</label>
                <input
                  type="date"
                  value={form.nextAuditDate}
                  onChange={(e) => setForm({ ...form, nextAuditDate: e.target.value })}
                  disabled={audit?.status === 'inchis'}
                  className="form-input"
                />
              </div>

              <div className="form-field full-width">
                <label className="form-label">{t('qms:nextAuditNotes') || 'Next Audit Notes'}</label>
                <textarea
                  value={form.nextAuditNotes}
                  onChange={(e) => setForm({ ...form, nextAuditNotes: e.target.value })}
                  disabled={audit?.status === 'inchis'}
                  className="form-textarea"
                  rows={3}
                  placeholder={t('qms:nextAuditNotesPlaceholder') || 'Notes for the next scheduled audit...'}
                />
              </div>

              <div className="form-field full-width">
                <label className="form-label required">{t('qms:ratingLabel')}</label>
                <StarRating
                  value={form.rating}
                  onChange={(r) => setForm({ ...form, rating: r })}
                  disabled={!canEdit('result')}
                />
              </div>
            </div>
          </section>
        )}

        {!isNew &&
          ['in_desfasurare', 'constatari', 'redeschis', 'inchis'].includes(audit?.status) && (
            <section className="aqd-section">
              <h3 className="aqd-section-title">{t('qms:photos')}</h3>
              <PhotoUploader
                auditId={auditId}
                userId={userId}
                photos={form.photos}
                onChange={(photos) => setForm({ ...form, photos })}
                disabled={audit?.status === 'inchis'}
              />
            </section>
          )}

        {!isNew && ['constatari', 'redeschis', 'inchis'].includes(audit?.status) && (
          <section className="aqd-section">
            <h3 className="aqd-section-title">{t('qms:notificationRecipients')}</h3>
            <p className="field-hint" style={{ marginBottom: 12 }}>
              {t('qms:recipientsTo')} / {t('qms:recipientsCc')}
            </p>
            <div className="form-field">
              <span className="form-label">{t('qms:recipientsTo')}</span>
              <div className="audit-recipient-chips">
                {(form.notificationRecipients?.to || []).map((em) => (
                  <span key={em} className="audit-recipient-chip">
                    {em}
                    {audit?.status !== 'inchis' && (
                      <button
                        type="button"
                        className="audit-recipient-chip-x"
                        aria-label={t('common:remove')}
                        onClick={() =>
                          setForm({
                            ...form,
                            notificationRecipients: {
                              ...form.notificationRecipients,
                              to: (form.notificationRecipients.to || []).filter((x) => x !== em)
                            }
                          })
                        }
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {audit?.status !== 'inchis' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    type="email"
                    className="form-input"
                    value={emailDraftTo}
                    onChange={(e) => setEmailDraftTo(e.target.value)}
                    placeholder={t('qms:addEmail')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = emailDraftTo.trim();
                        if (!v) return;
                        if ((form.notificationRecipients?.to || []).includes(v)) return;
                        setForm({
                          ...form,
                          notificationRecipients: {
                            ...form.notificationRecipients,
                            to: [...(form.notificationRecipients?.to || []), v]
                          }
                        });
                        setEmailDraftTo('');
                      }
                    }}
                  />
                </div>
              )}
            </div>
            <div className="form-field" style={{ marginTop: 16 }}>
              <span className="form-label">{t('qms:recipientsCc')}</span>
              <div className="audit-recipient-chips">
                {(form.notificationRecipients?.cc || []).map((em) => (
                  <span key={em} className="audit-recipient-chip">
                    {em}
                    {audit?.status !== 'inchis' && (
                      <button
                        type="button"
                        className="audit-recipient-chip-x"
                        aria-label={t('common:remove')}
                        onClick={() =>
                          setForm({
                            ...form,
                            notificationRecipients: {
                              ...form.notificationRecipients,
                              cc: (form.notificationRecipients.cc || []).filter((x) => x !== em)
                            }
                          })
                        }
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
              {audit?.status !== 'inchis' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <input
                    type="email"
                    className="form-input"
                    value={emailDraftCc}
                    onChange={(e) => setEmailDraftCc(e.target.value)}
                    placeholder={t('qms:addEmail')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = emailDraftCc.trim();
                        if (!v) return;
                        if ((form.notificationRecipients?.cc || []).includes(v)) return;
                        setForm({
                          ...form,
                          notificationRecipients: {
                            ...form.notificationRecipients,
                            cc: [...(form.notificationRecipients?.cc || []), v]
                          }
                        });
                        setEmailDraftCc('');
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </section>
        )}

        {!isNew && (
          <section className="aqd-section">
            <h3 className="aqd-section-title">{t('qms:signatures')}</h3>
            <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>{t('qms:signaturesPlaceholder')}</p>
          </section>
        )}
      </div>

      {/* Modals */}
      {showInstrumentModal && (
        <InstrumentSelectionModal
          instruments={instruments}
          onSelect={handleAddInstrument}
          onClose={() => setShowInstrumentModal(false)}
          t={t}
        />
      )}

      {showWitnessModal && (
        <WitnessModal
          users={users}
          onSave={handleAddWitness}
          onClose={() => setShowWitnessModal(false)}
          t={t}
        />
      )}

      {showActionModal && (
        <ActionModal
          users={users}
          onSave={handleAddAction}
          onClose={() => setShowActionModal(false)}
          t={t}
        />
      )}

      {showReopenModal && (
        <Modal isOpen={true} onClose={() => setShowReopenModal(false)} title={t('qms:reopenAudit') || 'Reopen Audit'}>
          <div className="reopen-modal">
            <p>{t('qms:reopenAuditDescription') || 'Provide a reason for reopening this closed audit.'}</p>
            
            <div className="form-field">
              <label className="form-label required">{t('qms:reopenNote') || 'Reopen Note'}</label>
              <textarea
                value={reopenNote}
                onChange={(e) => setReopenNote(e.target.value)}
                className="form-textarea"
                rows={4}
                placeholder={t('qms:reopenNotePlaceholder') || 'Explain why this audit needs to be reopened...'}
                minLength={10}
              />
              <p className="field-hint">{reopenNote.length}/10 {t('common:charactersMin') || 'characters minimum'}</p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowReopenModal(false);
                  setReopenNote('');
                }}
              >
                {t('common:cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleTransitionStatus('redeschis')}
                disabled={reopenNote.trim().length < 10}
              >
                {t('qms:reopen') || 'Reopen'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// Sub-components

const InstrumentDetailsView = ({ instance, instrument, t }) => {
  if (!instance || !instrument) return null;

  const { answers = {}, selectedThemeIds = [] } = instance;
  const components = instrument.components || instrument.themes || [];
  const ids = new Set((selectedThemeIds || []).filter(Boolean));
  const matched =
    ids.size > 0 ? components.filter((comp) => ids.has(comp.id)) : [];
  const selectedComponents = matched.length > 0 ? matched : components;

  const hasAnyStoredAnswer = Object.keys(answers).length > 0;
  const hasVisibleAnswer = selectedComponents.some((comp) =>
    (comp.questions || []).some((q) => getRawAnswerValue(answers, q) != null)
  );

  return (
    <div className="instrument-details-view">
      <h4 className="details-title">{t('qms:questionsAndAnswers') || 'Questions and Answers'}</h4>

      {hasAnyStoredAnswer && !hasVisibleAnswer && (
        <p className="instrument-answers-mismatch-hint" role="status">
          {t('qms:instrumentAnswersMismatch') ||
            'Răspunsurile salvate nu corespund acestui instrument (probabil completat cu UI greșit). Editați și retrimiteți evaluarea.'}
        </p>
      )}

      {selectedComponents.map((comp) => {
        const questions = comp.questions || [];

        return (
          <div key={comp.id} className="details-theme-section">
            <h5 className="theme-title">
              {comp.code && <span className="theme-code">{comp.code}.</span>}
              {comp.nameRo || comp.name}
            </h5>

            <div className="details-questions-list">
              {questions.map((q) => {
                const answerValue = formatAssessmentAnswer(answers, q, true);
                const rawVal = getRawAnswerValue(answers, q);
                const answerClass = answerDisplayClass(rawVal);

                return (
                  <div key={q.id} className="detail-question-row">
                    <div className="question-text">
                      <span className="question-code">{q.code}</span>
                      <span className="question-label">{q.textRo || q.text}</span>
                    </div>
                    <div className={`question-answer ${answerClass}`}>
                      {answerValue}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const InstrumentCard = ({ instance, canEdit, canComplete, onRemove, onComplete, t }) => {
  const getStatusIcon = (status) => {
    if (status === 'completed') return <CheckCircle size={16} className="status-icon-completed" />;
    if (status === 'in_progress') return <div className="status-icon-progress" />;
    return <div className="status-icon-pending" />;
  };

  const isCompleted = instance.status === 'completed';

  return (
    <div className="instrument-card">
      <div className="instrument-card-header">
        <div className="instrument-info">
          <h4>{instance.instrumentName}</h4>
          <div className="instrument-themes">
            {instance.selectedThemes?.map((theme) => (
              <span key={theme.id} className="theme-chip">{theme.name}</span>
            ))}
          </div>
        </div>
        <div className="instrument-status">
          {getStatusIcon(instance.status)}
          <span className="status-label">{t(`qms:instanceStatus.${instance.status}`) || instance.status}</span>
        </div>
      </div>

      <div className="instrument-card-actions">
        {canComplete && (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onComplete}
          >
            {isCompleted ? (t('qms:editAssessment') || 'Edit') : (t('qms:startAssessment') || 'Start')}
          </button>
        )}
        {canEdit && (
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={onRemove}
            title={
              isCompleted
                ? t('qms:removeInstrumentCompletedHint') ||
                  'Remove completed instrument from audit'
                : undefined
            }
          >
            <Trash2 size={14} />
            {t('common:remove') || 'Remove'}
          </button>
        )}
      </div>
    </div>
  );
};

const WitnessCard = ({ witness, canEdit, onRemove, t }) => {
  return (
    <div className="witness-card">
      <div className="witness-info">
        <User size={16} className="witness-icon" />
        <div>
          <div className="witness-name">{witness.name}</div>
          <div className="witness-role">{witness.role}</div>
          {witness.type === 'external' && (
            <span className="witness-badge">{t('qms:external') || 'External'}</span>
          )}
        </div>
      </div>
      {canEdit && (
        <button
          type="button"
          className="btn-icon-danger"
          onClick={onRemove}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
};

const ActionCard = ({ action, canEdit, onRemove, t }) => {
  const getTypeColor = (type) => {
    const colors = {
      masura: '#3b82f6',
      corectie: '#f59e0b',
      recomandare: '#10b981',
      capa: '#ef4444'
    };
    return colors[type] || '#6b7280';
  };

  return (
    <div className="action-card">
      <div className="action-header">
        <span 
          className="action-type-badge" 
          style={{ backgroundColor: getTypeColor(action.type) }}
        >
          {t(`qms:actionType.${action.type}`) || action.type}
        </span>
        <span className="action-status">{t(`qms:actionStatus.${action.status}`) || action.status}</span>
      </div>
      <p className="action-description">{action.description}</p>
      {action.assignedToName && (
        <div className="action-assigned">
          <User size={14} />
          {action.assignedToName}
          {action.dueDate && (
            <span className="action-due">
              <Calendar size={14} />
              {new Date(action.dueDate).toLocaleDateString('ro-RO')}
            </span>
          )}
        </div>
      )}
      {canEdit && (
        <button
          type="button"
          className="btn-icon-danger action-remove"
          onClick={onRemove}
        >
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
};

const InstrumentSelectionModal = ({ instruments, onSelect, onClose, t }) => {
  const { notify } = useNotifications();
  const [selectedInstrumentId, setSelectedInstrumentId] = useState('');
  const [selectedThemeIds, setSelectedThemeIds] = useState([]);
  const [step, setStep] = useState(1);

  const selectedInstrument = instruments.find((i) => i.id === selectedInstrumentId);
  const themes = selectedInstrument?.components || selectedInstrument?.themes || [];

  const handleNext = () => {
    if (!selectedInstrumentId) return;
    setStep(2);
  };

  const handleConfirm = () => {
    if (selectedThemeIds.length === 0) {
      notify.error(t('qms:selectAtLeastOneTheme') || 'Select at least one theme');
      return;
    }
    onSelect(selectedInstrumentId, selectedThemeIds);
  };

  const toggleTheme = (themeId) => {
    setSelectedThemeIds((prev) =>
      prev.includes(themeId)
        ? prev.filter((id) => id !== themeId)
        : [...prev, themeId]
    );
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={t('qms:selectInstrument') || 'Select Assessment Instrument'}>
      <div className="instrument-modal">

        {step === 1 && (
          <>
            <div className="instrument-list">
              {instruments.map((inst) => (
                <label key={inst.id} className="instrument-option">
                  <input
                    type="radio"
                    name="instrument"
                    value={inst.id}
                    checked={selectedInstrumentId === inst.id}
                    onChange={(e) => setSelectedInstrumentId(e.target.value)}
                  />
                  <div className="instrument-option-content">
                    <div className="instrument-option-name">{inst.nameRo || inst.name}</div>
                    <div className="instrument-option-meta">
                      {inst.totalQuestions || 0} {t('qms:questions') || 'questions'}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                {t('common:cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleNext}
                disabled={!selectedInstrumentId}
              >
                {t('common:next') || 'Next'}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <p className="modal-subtitle">
              {t('qms:selectThemes') || 'Select themes to include in this audit'}
            </p>

            <div className="theme-list">
              {themes.map((theme) => (
                <label key={theme.id} className="theme-option">
                  <input
                    type="checkbox"
                    checked={selectedThemeIds.includes(theme.id)}
                    onChange={() => toggleTheme(theme.id)}
                  />
                  <div className="theme-option-content">
                    <div className="theme-option-code">{theme.code || theme.id}</div>
                    <div className="theme-option-name">{theme.nameRo || theme.name}</div>
                    <div className="theme-option-meta">
                      {theme.questionIds?.length || theme.questions?.length || 0} {t('qms:questions') || 'questions'}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
                {t('common:back') || 'Back'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirm}
                disabled={selectedThemeIds.length === 0}
              >
                {t('common:confirm') || 'Confirm'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

const WitnessModal = ({ users, onSave, onClose, t }) => {
  const { notify } = useNotifications();
  const [witnessType, setWitnessType] = useState('system_user');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [externalName, setExternalName] = useState('');
  const [externalRole, setExternalRole] = useState('');
  const [externalPhone, setExternalPhone] = useState('');

  const handleSave = () => {
    if (witnessType === 'system_user') {
      if (!selectedUserId) {
        notify.error(t('qms:selectUser') || 'Select a user');
        return;
      }
      const user = users.find((u) => u.id === selectedUserId);
      onSave({
        id: `witness_${Date.now()}`,
        type: 'system_user',
        userId: selectedUserId,
        name: `${user.firstName} ${user.lastName}`,
        role: user.primaryRole || ''
      });
    } else {
      if (!externalName.trim() || !externalRole.trim()) {
        notify.error(t('qms:nameAndRoleRequired') || 'Name and role are required');
        return;
      }
      onSave({
        id: `witness_${Date.now()}`,
        type: 'external',
        userId: null,
        name: externalName.trim(),
        role: externalRole.trim(),
        phoneNumber: externalPhone.trim() || null
      });
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={t('qms:addWitness') || 'Add Witness'}>
      <div className="witness-modal">

        <div className="form-field">
          <label className="form-label">{t('qms:witnessType') || 'Witness Type'}</label>
          <select
            value={witnessType}
            onChange={(e) => setWitnessType(e.target.value)}
            className="form-input"
          >
            <option value="system_user">{t('qms:systemUser') || 'System User'}</option>
            <option value="external">{t('qms:externalPerson') || 'External Person'}</option>
          </select>
        </div>

        {witnessType === 'system_user' ? (
          <div className="form-field">
            <label className="form-label required">{t('qms:selectUser') || 'Select User'}</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="form-input"
            >
              <option value="">{t('common:select') || 'Select...'}</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} ({user.primaryRole})
                </option>
              ))}
            </select>
          </div>
        ) : (
          <>
            <div className="form-field">
              <label className="form-label required">{t('qms:name') || 'Name'}</label>
              <input
                type="text"
                value={externalName}
                onChange={(e) => setExternalName(e.target.value)}
                className="form-input"
                placeholder={t('qms:witnessNamePlaceholder') || 'Full name'}
              />
            </div>
            <div className="form-field">
              <label className="form-label required">{t('qms:role') || 'Role/Function'}</label>
              <input
                type="text"
                value={externalRole}
                onChange={(e) => setExternalRole(e.target.value)}
                className="form-input"
                placeholder={t('qms:witnessRolePlaceholder') || 'e.g., External Auditor, ANMCS Inspector'}
              />
            </div>
            <div className="form-field">
              <label className="form-label">{t('qms:witnessPhone')}</label>
              <input
                type="tel"
                value={externalPhone}
                onChange={(e) => setExternalPhone(e.target.value)}
                className="form-input"
                placeholder="+40722000000"
              />
              <small style={{ color: '#6b7280', fontSize: 12 }}>{t('qms:witnessPhoneHint')}</small>
            </div>
          </>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common:cancel') || 'Cancel'}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            {t('common:add') || 'Add'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const ActionModal = ({ users, onSave, onClose, t }) => {
  const { notify } = useNotifications();
  const [description, setDescription] = useState('');
  const [type, setType] = useState('masura');
  const [capaId, setCapaId] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [status, setStatus] = useState('open');

  const handleSave = () => {
    if (!description.trim()) {
      notify.error(t('qms:descriptionRequired') || 'Description is required');
      return;
    }

    const selectedUser = users.find((u) => u.id === assignedTo);
    
    onSave({
      id: `action_${Date.now()}`,
      description: description.trim(),
      type,
      capaId: type === 'capa' ? capaId.trim() || null : null,
      assignedTo: assignedTo || null,
      assignedToName: selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : null,
      dueDate: dueDate || null,
      status
    });
  };

  return (
    <Modal isOpen={true} onClose={onClose} title={t('qms:addAction') || 'Add Action'}>
      <div className="action-modal">

        <div className="form-field">
          <label className="form-label required">{t('qms:description') || 'Description'}</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-textarea"
            rows={3}
            placeholder={t('qms:actionDescriptionPlaceholder') || 'Describe the action to be taken...'}
          />
        </div>

        <div className="form-field">
          <label className="form-label required">{t('qms:actionType') || 'Type'}</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="form-input"
          >
            <option value="masura">{t('qms:actionType.masura') || 'Measure'}</option>
            <option value="corectie">{t('qms:actionType.corectie') || 'Correction'}</option>
            <option value="recomandare">{t('qms:actionType.recomandare') || 'Recommendation'}</option>
            <option value="capa">{t('qms:actionType.capa') || 'CAPA'}</option>
          </select>
        </div>

        {type === 'capa' && (
          <div className="form-field">
            <label className="form-label">{t('qms:capaId') || 'CAPA ID'}</label>
            <input
              type="text"
              value={capaId}
              onChange={(e) => setCapaId(e.target.value)}
              className="form-input"
              placeholder={t('qms:capaIdPlaceholder') || 'Optional CAPA reference'}
            />
          </div>
        )}

        <div className="form-field">
          <label className="form-label">{t('qms:assignedTo') || 'Assigned To'}</label>
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="form-input"
          >
            <option value="">{t('common:unassigned') || 'Unassigned'}</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
        </div>

        <div className="form-field">
          <label className="form-label">{t('qms:dueDate') || 'Due Date'}</label>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="form-input"
          />
        </div>

        <div className="form-field">
          <label className="form-label">{t('qms:status') || 'Status'}</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="form-input"
          >
            <option value="open">{t('qms:actionStatus.open') || 'Open'}</option>
            <option value="in_progress">{t('qms:actionStatus.in_progress') || 'In Progress'}</option>
            <option value="closed">{t('qms:actionStatus.closed') || 'Closed'}</option>
          </select>
        </div>

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('common:cancel') || 'Cancel'}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave}>
            {t('common:add') || 'Add'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AuditQMSDetail;
